import { Link } from 'react-router-dom'
import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function APIDocsPage() {

  const apiBaseUrl = 'https://api.gistmag.co.uk'
  
  const codeExamples = {
    curl: `curl -X POST ${apiBaseUrl}/generate \\
  -H "Content-Type: application/json" \\
  -d '{
    "image_url": "https://example.com/image.jpg",
    "mode": "short",
    "api_key": "your_api_key_here",
    "source": "api"
  }'`,
    javascript: `const response = await fetch('${apiBaseUrl}/generate', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    image_url: 'https://example.com/image.jpg',
    mode: 'short',
    api_key: 'your_api_key_here',
    source: 'api'
  })
})

const data = await response.json()
console.log(data.alt_text)`,
    python: `import requests

response = requests.post(
    '${apiBaseUrl}/generate',
    json={
        'image_url': 'https://example.com/image.jpg',
        'mode': 'short',
        'api_key': 'your_api_key_here',
        'source': 'api'
    }
)

data = response.json()
print(data['alt_text'])`,
    php: `<?php
$ch = curl_init('${apiBaseUrl}/generate');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'image_url' => 'https://example.com/image.jpg',
    'mode' => 'short',
    'api_key' => 'your_api_key_here',
    'source' => 'api'
]));

$response = curl_exec($ch);
$data = json_decode($response, true);
echo $data['alt_text'];
curl_close($ch);
?>`,
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-5xl font-bold mb-4 gradient-text">API Documentation</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Integrate GistMag's AI-powered alt text generation into your applications
          </p>
        </div>

        {/* Quick Start */}
        <section className="mb-12 glass-card rounded-xl p-8">
          <h2 className="text-3xl font-bold mb-4 flex items-center">
            <svg className="w-8 h-8 mr-3 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Quick Start
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Get your API key from the <Link to="/dashboard/api-key" className="text-primary-600 dark:text-primary-400 hover:underline">Dashboard</Link> and start generating alt text in minutes.
          </p>
          
          <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 overflow-x-auto">
            <pre className="text-green-400 text-sm">
              <code>{codeExamples.curl}</code>
            </pre>
          </div>
        </section>

        {/* Authentication */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-4">Authentication</h2>
          <div className="glass-card rounded-xl p-6 mb-4">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              All API requests require your API key. Include it in the request body as <code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">api_key</code>.
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                <strong>⚠️ Security:</strong> Never expose your API key in client-side code or public repositories. Use environment variables or secure storage.
              </p>
            </div>
          </div>
        </section>

        {/* Endpoints */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Endpoints</h2>

          {/* Generate Alt Text */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold">Generate Alt Text</h3>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold">POST</span>
            </div>
            <code className="text-sm text-gray-600 dark:text-gray-400 block mb-4">
              https://api.gistmag.co.uk/generate
            </code>
            
            <h4 className="font-semibold mb-2">Request Body</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  image_url: "string (required) - URL of the image",
                  mode: "string (optional) - 'short' | 'long' | 'seo' (default: 'short')",
                  language: "string (optional) - Language code (default: 'en')",
                  api_key: "string (required) - Your API key",
                  source: "string (optional) - 'web' | 'api' | 'extension' | 'wordpress' | 'shopify' | 'batch' (default: 'api')"
                }, null, 2)}</code>
              </pre>
            </div>

            <h4 className="font-semibold mb-2">Response</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  alt_text: "Generated alt text",
                  short_alt_text: "Short version (if mode is short)",
                  long_alt_text: "Long version (if mode is long)",
                  seo_alt_text: "SEO version (if mode is seo)",
                  image_url: "Original image URL",
                  generated_at: "ISO timestamp"
                }, null, 2)}</code>
              </pre>
            </div>
          </div>

          {/* Batch Processing */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold">Batch Processing</h3>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold">POST</span>
            </div>
            <code className="text-sm text-gray-600 dark:text-gray-400 block mb-4">
              https://api.gistmag.co.uk/batch
            </code>
            
            <h4 className="font-semibold mb-2">Request Body</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  images: [
                    {
                      image_url: "string (required)",
                      mode: "string (optional)",
                      language: "string (optional)",
                      source: "string (optional)"
                    }
                  ],
                  api_key: "string (required)"
                }, null, 2)}</code>
              </pre>
            </div>

            <h4 className="font-semibold mb-2">Response</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  results: ["Array of alt text responses"],
                  total: "Total number of images",
                  successful: "Number of successful generations",
                  failed: "Number of failed generations"
                }, null, 2)}</code>
              </pre>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold">Usage Statistics</h3>
              <div className="flex gap-2">
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold">GET</span>
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-semibold">POST</span>
              </div>
            </div>
            <code className="text-sm text-gray-600 dark:text-gray-400 block mb-4">
              https://api.gistmag.co.uk/usage
            </code>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Supports both GET (with query parameter) and POST (with request body) methods.
            </p>
            
            <h4 className="font-semibold mb-2">GET Request (Query Parameter)</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{`https://api.gistmag.co.uk/usage?api_key=your_api_key_here`}</code>
              </pre>
            </div>

            <h4 className="font-semibold mb-2">POST Request Body</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  api_key: "string (required) - Your API key"
                }, null, 2)}</code>
              </pre>
            </div>

            <h4 className="font-semibold mb-2">Response</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  current_month: "Number of images used this month",
                  monthly_limit: "Your monthly limit",
                  remaining: "Remaining images this month",
                  subscription_tier: "Your subscription tier",
                  by_source: {
                    web: 0,
                    api: 0,
                    extension: 0,
                    wordpress: 0,
                    shopify: 0,
                    batch: 0
                  }
                }, null, 2)}</code>
              </pre>
            </div>
          </div>

          {/* Verify API Key */}
          <div className="glass-card rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold">Verify API Key</h3>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold">POST</span>
            </div>
            <code className="text-sm text-gray-600 dark:text-gray-400 block mb-4">
              https://api.gistmag.co.uk/verify-key
            </code>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Check if an API key is valid and get subscription information.
            </p>
            
            <h4 className="font-semibold mb-2">Request Body</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  api_key: "string (required) - Your API key"
                }, null, 2)}</code>
              </pre>
            </div>

            <h4 className="font-semibold mb-2">Response</h4>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">
                <code>{JSON.stringify({
                  valid: true,
                  subscription_tier: "free",
                  message: "API key is valid"
                }, null, 2)}</code>
              </pre>
            </div>
          </div>
        </section>

        {/* Code Examples */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6">Code Examples</h2>
          
          <div className="space-y-6">
            {Object.entries(codeExamples).map(([lang, code]) => (
              <div key={lang} className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold capitalize">{lang}</h3>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(code)
                      alert('Copied to clipboard!')
                    }}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Copy
                  </button>
                </div>
                <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-400 text-sm">
                    <code>{code}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Rate Limits */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-4">Rate Limits</h2>
          <div className="glass-card rounded-xl p-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Free Plan</h4>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">50</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">images (one-time)</p>
              </div>
              <div className="border border-primary-200 dark:border-primary-800 rounded-lg p-4 bg-primary-50 dark:bg-primary-900/20">
                <h4 className="font-semibold mb-2">Pro Plan</h4>
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1">1,000</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">images/month</p>
              </div>
              <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Enterprise</h4>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">100,000</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">images/month</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              Free plan credits are one-time. Paid plans reset monthly. Upgrade your plan in the <Link to="/dashboard" className="text-primary-600 dark:text-primary-400 hover:underline">Dashboard</Link>.
            </p>
          </div>
        </section>

        {/* Error Handling */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-4">Error Handling</h2>
          <div className="glass-card rounded-xl p-6">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              All errors follow a consistent format:
            </p>
            <div className="bg-gray-900 dark:bg-slate-800 rounded-lg p-4 mb-4 overflow-x-auto">
              <pre className="text-red-400 text-sm">
                <code>{JSON.stringify({
                  error: "Error type",
                  message: "Human-readable error message"
                }, null, 2)}</code>
              </pre>
            </div>
            
            <h4 className="font-semibold mb-2 mt-6">Common Error Codes</h4>
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-semibold">401</span>
                <div>
                  <p className="font-medium">Unauthorized</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Invalid or missing API key</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-semibold">400</span>
                <div>
                  <p className="font-medium">Bad Request</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Invalid request parameters</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-sm font-semibold">429</span>
                <div>
                  <p className="font-medium">Rate Limit Exceeded</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Monthly limit reached</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-sm font-semibold">500</span>
                <div>
                  <p className="font-medium">Internal Server Error</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Server error, please try again</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Source Tracking */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-4">Source Tracking</h2>
          <div className="glass-card rounded-xl p-6">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Track where your API calls are coming from by including the <code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">source</code> parameter:
            </p>
            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">web</code> - Web dashboard</li>
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">api</code> - Direct API calls</li>
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">extension</code> - Browser extension</li>
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">wordpress</code> - WordPress plugin</li>
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">shopify</code> - Shopify app</li>
              <li><code className="bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded">batch</code> - Batch processing</li>
            </ul>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              View your usage breakdown by source in the <Link to="/dashboard/analytics" className="text-primary-600 dark:text-primary-400 hover:underline">Analytics</Link> page.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center glass-card rounded-xl p-8">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Sign up for a free account and get your API key in minutes
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/auth/signup"
              className="px-8 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-primary-600 transition"
            >
              Get Started Free
            </Link>
            <Link
              to="/dashboard/api-key"
              className="px-8 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition"
            >
              View API Key
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  )
}

